import { cast, Instance, SnapshotIn, types } from "mobx-state-tree";

import {
  isArraySchema,
  ObjectPropertyIsMapType,
  ObjectSchemaType,
  WithArraySchema,
} from "../hooks/use-form";
import { Control, IControl, IControlProperties, InputType } from "./control";

// Type definitions for form structure
export const ControlsMap = types.map(Control);
export const ArrayControlsMap = types.array(ControlsMap);
export interface IControlsMap extends Instance<typeof ControlsMap> {}

/**
 * Main Form model using MobX-State-Tree
 * Manages a collection of form controls with validation, submission, and data management
 */
export const Form = types
  .model({
    /** Map of form controls indexed by their names */
    controls: types.optional(ControlsMap, {}),
  })
  .views((self) => ({
    /**
     * Computed view that converts the controls map to an array
     * @returns Array of all form controls
     */
    get controlAsArray(): IControl[] {
      return Array.from(self.controls.values());
    },
  }))
  .views((self) => ({
    /**
     * Computed view for overall form validation status
     * @returns true if all controls in the form are valid
     */
    get isValid() {
      const invalid =
        (self.controlAsArray.filter((x) => !x.isValid)?.length || 0) > 0;

      return !invalid;
    },
    /**
     * Computed view for overall form dirty status
     * @returns true if any control in the form has been modified
     */
    get isDirty() {
      const inDirty =
        (self.controlAsArray.filter((x) => x.isDirty)?.length || 0) > 0;
      return inDirty;
    },
  }))
  .actions((self) => ({
    /**
     * Creates child controls for array-type fields
     * Generates nested control structures based on schema and data
     * @param childSchema - Schema definition for child controls
     * @param itemsValue - Array of data values to populate children
     * @returns 2D array of control properties for children
     */
    createChidrenOfArray: (
      childSchema: ObjectPropertyIsMapType<any>,
      itemsValue: any
    ) => {
      const children: IControlProperties[][] = [];
      itemsValue.forEach((item: object) => {
        const child: IControlProperties[] = [];
        Object.keys(childSchema).forEach((key) => {
          const controlInArray = childSchema[key];
          const defaultValueOfChild =
            (item as any)?.[key] || controlInArray.defaultValue;
          const newControlInArray: IControlProperties = {
            ...controlInArray,
            name: key,
            defaultValue: defaultValueOfChild,
            value: defaultValueOfChild,
          };
          child.push(newControlInArray);
        });
        children.push(child);
      });
      return children;
    },
  }))
  .actions((self) => ({
    /**
     * Sets default values for all form controls
     * Handles both simple controls and array-type controls with nested data
     * @param defaults - Object containing default values keyed by control names
     */
    setDefaults: (defaults: any) => {
      self.controls.forEach((x) => {
        if (x.type === InputType.array || isArraySchema(x)) {
          const arrayValue: any = defaults[x.name];
          const childSchema = x.childSchema;
          const children = self.createChidrenOfArray(childSchema, arrayValue);
          x.setChildren(cast(children));
        } else {
          x.setProp("defaultValue", defaults?.[x.name]);
          x.setProp("value", defaults?.[x.name]);
        }
      });
    },
  }))
  .actions((self) => ({
    /**
     * Initializes the form with control schema and optional default values
     * Creates control instances based on schema definition
     * @param object - Schema object defining all form controls
     * @param defaults - Optional default values for form fields
     */
    initForm: (object: ObjectSchemaType<any>, defaults?: any) => {
      Object.keys(object).forEach((key) => {
        const defaultVal = defaults?.[key] as any;
        const control = object[key];
        const newControl: IControlProperties = {
          ...control,
          name: key,
          defaultValue: defaultVal,
          value: defaultVal,
        };

        // Handle array-type controls with nested schema
        if (control.type === InputType.array || isArraySchema(control)) {
          newControl.type = InputType.array;
          const controlArray = control as WithArraySchema<any>;
          const childSchema = controlArray.arraySchema;
          newControl.childSchema = childSchema;
          const children = self.createChidrenOfArray(childSchema, defaultVal);
          newControl.children = children;
        }
        self.controls.set(key, newControl);
      });
    },

    /**
     * Validates all controls in the form
     * @returns Promise that resolves when all validations complete
     */
    validate: async () => {
      const propmises = self.controlAsArray.map((x) => [x.validate()]);
      await Promise.all(propmises);
    },

    /**
     * Resets all form controls to their default values
     * Handles both simple and array-type controls
     */
    reset: () => {
      self.controlAsArray.forEach((x) => {
        if (x.type === InputType.array) {
          x.children.forEach((x) =>
            x.forEach((c: IControl) => {
              c.value = c.defaultValue;
            })
          );
        }
        x.value = x.defaultValue;
      });
    },

    /**
     * Retrieves a specific control by its name
     * @param key - Name of the control to retrieve
     * @returns The control instance
     */
    getControl: (key: string): IControl => {
      return self.controls.get(key) as IControl;
    },
  }))
  .actions((self) => ({
    /**
     * Submits the form after validation
     * Collects all form data and calls the provided callback if form is valid
     * @param callback - Function to handle the submitted form data
     * @returns Promise that resolves after submission attempt
     */
    submit: async <T>(callback: (data: T) => Promise<void>) => {
      await self.validate();
      if (self.isValid) {
        const data: any = {};

        // Collect data from all controls
        self.controlAsArray.forEach((x) => {
          if (x.type === InputType.array) {
            // Handle array controls - collect nested data
            const array: any = [];
            x.children.forEach((row: IControl[]) => {
              const item: any = {};
              row.forEach((ctr: IControl) => {
                item[ctr.name] = ctr.value;
              });
              array.push(item);
            });
            data[x.name] = array;
          } else {
            // Handle simple controls
            data[x.name] = x.value;
          }
        });
        await callback(data);
      } else {
        console.log("Invalid field");
      }
    },
  }));

// Type definitions for external usage
export interface IForm extends Instance<typeof Form> {}
export interface IFormProperties extends SnapshotIn<typeof Form> {}
